import type { ReactNode } from 'react'
import { useEchap } from './Toast'

export type Couleur = 'neutre' | 'ok' | 'warn' | 'stop' | 'act' | 'hw'

const cls = (c: Couleur) => (c === 'neutre' ? '' : ` ${c}`)

export const Lamp = ({ c = 'neutre', pulse = false }: { c?: Couleur; pulse?: boolean }) => (
  <span className={`lamp${cls(c)}${pulse ? ' pulse' : ''}`} aria-hidden="true" />
)

export const Chip = ({ c = 'neutre', children }: { c?: Couleur; children: ReactNode }) => (
  <span className={`chip${cls(c)}`}>{children}</span>
)

export function Card({ titre, lampe, actions, children, style, ancre }: {
  titre?: string; lampe?: Couleur; actions?: ReactNode; children: ReactNode
  style?: React.CSSProperties
  /** Identifiant DOM, pour qu'un lien puisse amener l'écran jusqu'à ce bloc. */
  ancre?: string
}) {
  return (
    <div className="card" id={ancre} style={style}>
      {titre && (
        <div className="card-hd">
          {lampe && <Lamp c={lampe} />}
          <h3>{titre}</h3>
          <span className="sp" />
          {actions}
        </div>
      )}
      {children}
    </div>
  )
}

export function Kpis({ items }: {
  items: Array<{ k: string; v: ReactNode; s?: string; couleur?: string }>
}) {
  return (
    <div className="kpis">
      {items.map((it) => (
        <div className="kpi" key={it.k}>
          <div className="k">{it.k}</div>
          <div className="v" style={it.couleur ? { color: it.couleur } : undefined}>{it.v}</div>
          {it.s && <div className="s">{it.s}</div>}
        </div>
      ))}
    </div>
  )
}

export function Notice({ c = 'neutre', children, action }: {
  c?: Couleur; children: ReactNode; action?: ReactNode
}) {
  return (
    <div className={`notice${c === 'neutre' ? '' : ` ${c}`}`}>
      <Lamp c={c} />
      <div className="sp">{children}</div>
      {action}
    </div>
  )
}

/** Bandeau « en construction » — la place est tenue, le contenu viendra (§7.6). */
export function Chantier({ titre, children }: { titre: string; children: ReactNode }) {
  return (
    <div className="chantier">
      <Chip c="act">EN CONSTRUCTION</Chip>
      <div className="t" style={{ marginTop: 8 }}>{titre}</div>
      <div className="d">{children}</div>
    </div>
  )
}

export const Vide = ({ children }: { children: ReactNode }) => <div className="empty">{children}</div>

export function Modale({ titre, sous, children, fermer, large = false }: {
  titre: string; sous?: string; children: ReactNode; fermer: () => void; large?: boolean
}) {
  useEchap(true, fermer)
  return (
    <div className="ov" onMouseDown={(e) => { if (e.target === e.currentTarget) fermer() }}>
      <div className={`mod${large ? ' lg' : ''}`} role="dialog" aria-modal="true" aria-label={titre}>
        <h2>{titre}</h2>
        {sous && <p>{sous}</p>}
        {children}
      </div>
    </div>
  )
}

/** Le pavé « ce qui est repris de la V2 » en tête d'une étape non encore portée. */
export function Reprise({ children }: { children: ReactNode }) {
  return (
    <div className="notice ro" style={{ alignItems: 'flex-start' }}>
      <Lamp />
      <div className="sp">
        <b>Reprise du fonctionnel V2</b>
        <div style={{ color: 'var(--ink3)', marginTop: 2 }}>{children}</div>
      </div>
    </div>
  )
}
